import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import OverviewCard from '../components/OverviewCard';
import OverviewModal from '../components/OverviewModal';
import ConfirmDialog from '../components/ConfirmDialog';

const HomeExtensionLoanOverviewPage = () => {
    const [overviews, setOverviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOverview, setEditingOverview] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, overviewId: null });

    const LOAN_TYPE = 'home_extension_loan';

    // Fetch overviews
    const fetchOverviews = async () => {
        try {
            setLoading(true);
            // Get overview for this specific loan type
            const response = await axios.get(`/api/admin/loan-overview/${LOAN_TYPE}`);
            const data = response.data.data;
            // Wrap single item in array for consistent grid display
            setOverviews(data ? [data] : []);
        } catch (error) {
            // If not found (404), just show empty state
            if (error.response?.status === 404) {
                setOverviews([]);
            } else {
                console.error('Error fetching overview:', error);
                toast.error('Failed to load overview');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOverviews();
    }, []);

    // Handle add new overview
    const handleAddNew = () => {
        setEditingOverview(null);
        setIsModalOpen(true);
    };

    // Handle edit overview
    const handleEdit = (overview) => {
        setEditingOverview(overview);
        setIsModalOpen(true);
    };

    // Handle delete overview
    const handleDelete = (overviewId) => {
        setDeleteConfirm({ isOpen: true, overviewId });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/loan-overview/${deleteConfirm.overviewId}`);
            toast.success('Overview deleted successfully');
            fetchOverviews();
        } catch (error) {
            console.error('Error deleting overview:', error);
            toast.error('Failed to delete overview');
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingOverview(null);
    };

    // Handle save (create or update)
    const handleSave = () => {
        handleModalClose();
        fetchOverviews();
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Home Extension Loan - Overview</h1>
                    <p className="text-slate-500 mt-1">Manage the overview section content</p>
                </div>
                {overviews.length === 0 && (
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add Overview
                    </button>
                )}
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
                            <div className="aspect-video bg-slate-200 rounded-xl mb-4"></div>
                            <div className="h-6 bg-slate-200 rounded mb-2"></div>
                            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                        </div>
                    ))}
                </div>
            ) : overviews.length === 0 ? (
                /* Empty State */
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <PlusIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-800 mb-2">No overview yet</h3>
                        <p className="text-slate-500 mb-6">
                            Get started by creating the overview content for Home Extension Loan
                        </p>
                        <button
                            onClick={handleAddNew}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                        >
                            Create Overview
                        </button>
                    </div>
                </div>
            ) : (
                /* Overviews Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {overviews.map((overview) => (
                        <OverviewCard
                            key={overview.id}
                            overview={overview}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Overview Modal */}
            {isModalOpen && (
                <OverviewModal
                    overview={editingOverview}
                    loanType={LOAN_TYPE}
                    onClose={handleModalClose}
                    onSave={handleSave}
                />
            )}

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, overviewId: null })}
                onConfirm={confirmDelete}
                title="Delete Overview"
                message="Are you sure you want to delete this overview? This action cannot be undone."
            />
        </div>
    );
};

export default HomeExtensionLoanOverviewPage;
